import toNumber from 'lodash/toNumber'
import toString from 'lodash/toString'
import { Check, Search, X } from 'lucide-react'
import { DOCUMENT_TYPE_LABEL, DOCUMENT_TYPES, type DocumentType } from '@klear/constants/document-types'
import { Badge } from '@klear/ui/components/badge'
import { Button } from '@klear/ui/components/button'
import { Input } from '@klear/ui/components/input'
import { Popover, PopoverContent, PopoverTrigger } from '@klear/ui/components/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@klear/ui/components/command'
import { cn } from '@klear/ui/lib/utils'

type MultiSelectFilterProps = {
  label: string
  options: { value: string; label: string }[]
  selected: readonly string[]
  onToggle: (value: string) => void
  onClear: () => void
  searchable?: boolean
}

function MultiSelectFilter({ label, options, selected, onToggle, onClear, searchable }: MultiSelectFilterProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 border-dashed">
          {label}
          {selected.length > 0 && (
            <Badge variant="secondary" className="ml-2 rounded-sm px-1 font-normal">
              {selected.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-0" align="start">
        <Command>
          {searchable && <CommandInput placeholder={`Filter ${label.toLowerCase()}…`} />}
          <CommandList>
            <CommandEmpty>No results.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selected.includes(option.value)
                return (
                  <CommandItem key={option.value} onSelect={() => onToggle(option.value)}>
                    <div
                      className={cn(
                        'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                        isSelected ? 'bg-primary text-primary-foreground' : 'opacity-50 [&_svg]:invisible'
                      )}
                    >
                      <Check className="h-4 w-4" />
                    </div>
                    <span>{option.label}</span>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
        {selected.length > 0 && (
          <div className="border-t p-1">
            <Button variant="ghost" size="sm" className="w-full justify-center" onClick={onClear}>
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

export function DocumentTypeFilter({
  selected,
  onChange
}: {
  selected: readonly DocumentType[]
  onChange: (types: DocumentType[]) => void
}) {
  return (
    <MultiSelectFilter
      label="Type"
      options={DOCUMENT_TYPES.map((type) => ({ value: type, label: DOCUMENT_TYPE_LABEL[type] }))}
      selected={selected}
      onToggle={(value) => onChange(DOCUMENT_TYPES.filter((type) => (type === value) !== selected.includes(type)))}
      onClear={() => onChange([])}
    />
  )
}

type DocumentToolbarProps = {
  search: string
  onSearchChange: (value: string) => void
  traderOptions: readonly { id: number; name: string }[]
  selectedTraderId: number | undefined
  onSelectTraderId: (traderId: number | undefined) => void
  selectedTypes: readonly DocumentType[]
  onSelectTypes: (types: DocumentType[]) => void
}

export function DocumentToolbar({
  search,
  onSearchChange,
  traderOptions,
  selectedTraderId,
  onSelectTraderId,
  selectedTypes,
  onSelectTypes
}: DocumentToolbarProps) {
  const hasActiveFilters = search !== '' || selectedTraderId !== undefined || selectedTypes.length > 0

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search documents…"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          className="h-9 w-[260px] pl-8"
        />
      </div>
      {traderOptions.length > 0 && (
        <MultiSelectFilter
          label="Trader"
          options={traderOptions.map((trader) => ({ value: toString(trader.id), label: trader.name }))}
          selected={selectedTraderId !== undefined ? [toString(selectedTraderId)] : []}
          onToggle={(value) => {
            const traderId = toNumber(value)
            onSelectTraderId(traderId === selectedTraderId ? undefined : traderId)
          }}
          onClear={() => onSelectTraderId(undefined)}
          searchable
        />
      )}
      <DocumentTypeFilter selected={selectedTypes} onChange={onSelectTypes} />
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          className="h-9 px-2"
          onClick={() => {
            onSearchChange('')
            onSelectTraderId(undefined)
            onSelectTypes([])
          }}
        >
          Reset
          <X className="ml-1 h-4 w-4" />
        </Button>
      )}
    </div>
  )
}
